import * as React from 'react';
import { SelectOption } from 'formComponents/Select/types';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { Select } from 'formComponents/Select/Select';
import { ValueType } from 'react-select';
import { formStyles } from 'styles/formStyles';
import { DefaultButton, Dialog, DialogFooter, DialogType, Fabric, PrimaryButton } from '@fluentui/react';
import { addTextbaustein, defaultTextbaustein, Textbaustein, updateTextbaustein } from 'api/textbaustein';
import { useSelector } from 'react-redux';
import { textbausteineSelector } from 'store/lookups/selector';
import { viewStyles } from 'styles/viewStyles';
import { FormTextField } from 'formComponents/FormTextField';
import { SavingState } from 'api/savingState';
import { FormTextarea } from 'formComponents/FormTextarea';
import { requestTextbausteine } from 'store/lookups/actions';

const styles = mergeStyleSets({
    container: {
        position: 'relative',
        height: 'calc(100vh - 120px)',
        display: 'grid',
        gridTemplateRows: 'max-content 1fr',
        gridRowGap: 10,
    },
    searchBar: {
        display: 'grid',
        gridTemplateColumns: '300px 1fr max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
});

const groupOptions: SelectOption[] = [
    { value: '11', label: 'Lieferbedingung' },
    { value: '12', label: 'Zahlungsbedingungen (Lieferant)' },
    { value: '14', label: 'Lieferzeit' },
    { value: '21', label: 'Angebot' },
    { value: '22', label: 'Auftrag' },
    { value: '23', label: 'Bestellung' },
    { value: '24', label: 'Lieferung' },
    { value: '25', label: 'Rechnung' },
];

export const Textbausteine: React.FC = () => {
    const [selectedGroupOption, setSelectedGroupOption] = React.useState(groupOptions[0]);

    const textbausteine = useSelector(textbausteineSelector);

    const [editData, setEditData] = React.useState<Textbaustein | null>(null);

    const filteredTextbausteine = React.useMemo(() => {
        return textbausteine.filter((t) => t.kz.toString() === selectedGroupOption.value);
    }, [textbausteine, selectedGroupOption.value]);

    const save = async () => {
        if (!editData) {
            return;
        }
        if (editData?.nr === -1) {
            await addTextbaustein(editData);
        } else {
            await updateTextbaustein(editData);
        }
        await requestTextbausteine();
        setEditData(null);
    };

    return (
        <Fabric className={styles.container}>
            <div className={styles.searchBar}>
                <Select
                    name="Gruppe"
                    value={selectedGroupOption}
                    options={groupOptions}
                    onChange={(newValue: ValueType<SelectOption, false>): void => {
                        if (newValue) {
                            setSelectedGroupOption(newValue);
                        }
                    }}
                />

                <div />

                <PrimaryButton
                    onClick={() =>
                        setEditData({ ...defaultTextbaustein, kz: parseInt(selectedGroupOption.value, 10), nr: -1 })
                    }
                    text="Neuer Textbaustein"
                />
            </div>
            <div className={viewStyles.listContainerScroll}>
                <table className={formStyles.lookupTable}>
                    <thead className={formStyles.lookupTableHead}>
                        <tr>
                            <td className={formStyles.lookupTableCell}>Name</td>
                            <td className={formStyles.lookupTableCell}>Kurz</td>
                            <td className={formStyles.lookupTableCell}>Lang</td>
                            <td className={formStyles.lookupTableCell} />
                        </tr>
                    </thead>
                    <tbody className={formStyles.lookupTableBody}>
                        {filteredTextbausteine
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((textbaustein) => (
                                <tr key={textbaustein.nr} className={formStyles.looupTableRow}>
                                    <td className={formStyles.lookupTableCell}>{textbaustein.name}</td>
                                    <td className={formStyles.lookupTableCell}>{textbaustein.kurz}</td>
                                    <td className={formStyles.lookupTableCell}>{textbaustein.lang}</td>
                                    <td className={formStyles.lookupTableActionCell}>
                                        <DefaultButton
                                            className={formStyles.lookupTableEditButton}
                                            onClick={() => setEditData({ ...textbaustein })}
                                            text="Bearbeiten"
                                        />
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
            {editData && (
                <Dialog
                    hidden={false}
                    minWidth={800}
                    dialogContentProps={{
                        title: 'Textbaustein bearbeiten',
                        type: DialogType.normal,
                    }}
                >
                    <FormTextField
                        value={editData.name}
                        savingState={SavingState.notChanged}
                        options={{}}
                        onChange={(newValue): void => {
                            if (editData) {
                                setEditData({
                                    ...editData,
                                    name: newValue as string,
                                });
                            }
                        }}
                        label="Kurz"
                    />
                    <FormTextField
                        value={editData.kurz}
                        savingState={SavingState.notChanged}
                        options={{}}
                        onChange={(newValue): void => {
                            if (editData) {
                                setEditData({
                                    ...editData,
                                    kurz: newValue as string,
                                });
                            }
                        }}
                        label="Kurz"
                    />
                    <FormTextarea
                        value={editData.lang || ''}
                        label="Lang"
                        onChange={(newValue) => {
                            if (editData) {
                                setEditData({
                                    ...editData,
                                    lang: newValue as string,
                                });
                            }
                        }}
                        savingState={SavingState.notChanged}
                        options={{}}
                    />
                    <DialogFooter>
                        <DefaultButton onClick={() => setEditData(null)} text="Abbruch" />
                        <PrimaryButton onClick={save} text="Speichern" />
                    </DialogFooter>
                </Dialog>
            )}
        </Fabric>
    );
};
